// income.js — ONE home for income cadence (2026-10-14).
//
// Scorify never detected paydays from transactions. "Paydays" were a manual pay
// schedule (finances.pay_freq / pay_day1 / pay_day2 / pay_weekday / pay_start)
// that DEFAULTED to biweekly anchored on the 1st, and the Cash Flow Calendar
// painted PAY badges from that default even when the user never set one — so a
// freelancer saw two or three invented paydays a month. Income itself is a
// rolling 30-day sum of countable deposits, which swings wildly for anyone paid
// irregularly, and the "Is this your income?" cards labeled each client's
// deposits with a guessed cadence.
//
// This module classifies a user's income as REGULAR (a stable cadence with
// similar amounts) or IRREGULAR, and gives irregular earners an honest view:
// income received this month, a typical monthly range from their own history,
// and whether this month is tracking above or below. A manual override
// (finances.income_type_override) always wins over detection.
//
// Pure, no imports: used by src/Scorify.jsx and by api/lib/coach-run.js (the
// api side imports src/lib/* directly, like spendLabels.js).

// Normalized key for matching Plaid inflow names against income_rules rows:
// uppercase, digits and punctuation stripped, whitespace collapsed, first 4
// tokens. MATCHED PAIR with income_rules.match_name (supabase/migrations/
// 20260910_income_rules.sql): stored rows are keys produced by THIS function;
// change it and existing rules stop matching. Moved here from Scorify.jsx so
// the server can group deposits the same way.
import { txLocalDay } from './spend.js';

export const normalizeInflowName = (name) => (name || "")
  .toUpperCase()
  .replace(/[0-9]/g, " ")
  .replace(/[^A-Z ]/g, " ")
  .replace(/\s+/g, " ")
  .trim()
  .split(" ")
  .slice(0, 4)
  .join(" ");

// THE single income-countability decision for a depository INFLOW (the caller
// filters account type and amount < 0). Rule-first: a confirmed income_rules
// classification wins; otherwise the category default: TRANSFER_OUT and
// LOAN_PAYMENTS never count, app transfers (Venmo/Zelle) count only when
// confirmed, mobile-deposit paychecks and everything else count. Moved here
// from Scorify.jsx (same body) so client and server cannot diverge.
export const isCountableInflow = (t, ruleByName) => {
  const rule = ruleByName?.get ? ruleByName.get(normalizeInflowName(t.name)) : undefined;
  if (rule === "income") return true;
  if (rule === "not_income") return false;
  const primary = t.personal_finance_category?.primary || "";
  if (primary === "TRANSFER_OUT" || primary === "LOAN_PAYMENTS") return false;
  if (t.personal_finance_category?.detailed === "TRANSFER_IN_TRANSFER_IN_FROM_APPS") return false;
  return true;
};

/** Countable deposits from a Plaid transaction list: [{ date, amount (>0), name, key }]. */
export function countableDeposits(transactions, { depositoryAccountIds, ruleByName } = {}) {
  const ids = depositoryAccountIds ? new Set(depositoryAccountIds) : null;
  const out = [];
  for (const t of transactions || []) {
    if (!t || !(t.amount < 0)) continue;
    if (ids && !ids.has(t.account_id)) continue;
    const date = String(txLocalDay(t) || "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;
    if (!isCountableInflow(t, ruleByName || new Map())) continue;
    out.push({ date, amount: Math.abs(Number(t.amount) || 0), name: t.name || "", key: normalizeInflowName(t.name) });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

const DAY = 86400000;
const daysBetween = (a, b) => Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / DAY);
const median = (xs) => { if (!xs.length) return null; const s = [...xs].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; };
const percentile = (xs, p) => { if (!xs.length) return null; const s = [...xs].sort((a, b) => a - b); return s[Math.min(s.length - 1, Math.max(0, Math.ceil(p * s.length) - 1))]; };

// Detection bars. Conservative on purpose: a false "regular" invents paydays, a
// false "irregular" only hides them, so ties go to irregular once history is
// long enough, and to "unknown" before that.
export const INCOME_MIN_DEPOSITS = 6;       // countable deposits before any verdict
export const INCOME_MIN_SPAN_DAYS = 60;     // history span before any verdict
const MIN_DEPOSIT = 50;                     // ignore small credits (refunds, interest)
const GROUP_MIN_DEPOSITS = 4;               // a paycheck source needs this many to read a cadence
const GAP_STEADY_SHARE = 0.7;               // share of gaps within tolerance of the median gap
const AMOUNT_STEADY_SHARE = 0.7;            // share of amounts within ±30% of the median amount
const AMOUNT_TOLERANCE = 0.30;
const CADENCE_BANDS = [
  { cadence: "weekly", lo: 5, hi: 9, tol: 2 },
  { cadence: "biweekly", lo: 12, hi: 17, tol: 3 },   // semimonthly reads here too (13-17 day gaps)
  { cadence: "monthly", lo: 26, hi: 35, tol: 4 },
];

/**
 * Classify a user's income cadence from their countable deposits.
 * @param {Array<{date:string, amount:number, name?:string, key?:string}>} deposits
 * @returns {{ type:'regular'|'irregular'|'unknown', cadence:string|null, source:string|null, reason:string, depositsAnalyzed:number, spanDays:number }}
 */
export function classifyIncomeCadence(deposits = []) {
  const rows = (deposits || []).filter(d => d && d.amount >= MIN_DEPOSIT && /^\d{4}-\d{2}-\d{2}$/.test(d.date))
    .sort((a, b) => a.date.localeCompare(b.date));
  const spanDays = rows.length ? daysBetween(rows[0].date, rows[rows.length - 1].date) : 0;
  const base = { cadence: null, source: null, depositsAnalyzed: rows.length, spanDays };
  if (rows.length < INCOME_MIN_DEPOSITS || spanDays < INCOME_MIN_SPAN_DAYS) {
    return { ...base, type: "unknown", reason: `not enough history (${rows.length} deposits over ${spanDays} days; needs ${INCOME_MIN_DEPOSITS} over ${INCOME_MIN_SPAN_DAYS})` };
  }
  // Group by source; same source + same day collapses to one deposit (a split paycheck).
  const groups = new Map();
  for (const d of rows) {
    const key = d.key || normalizeInflowName(d.name);
    if (!groups.has(key)) groups.set(key, new Map());
    const byDay = groups.get(key);
    byDay.set(d.date, (byDay.get(d.date) || 0) + d.amount);
  }
  const ranked = [...groups.entries()]
    .map(([key, byDay]) => ({ key, deposits: [...byDay.entries()].map(([date, amount]) => ({ date, amount })).sort((a, b) => a.date.localeCompare(b.date)) }))
    .map(g => ({ ...g, total: g.deposits.reduce((s, d) => s + d.amount, 0) }))
    .sort((a, b) => b.total - a.total);
  // The largest source decides: a salaried person with side gigs is still regular.
  for (const g of ranked.slice(0, 2)) {
    if (g.deposits.length < GROUP_MIN_DEPOSITS) continue;
    const gaps = [];
    for (let i = 1; i < g.deposits.length; i++) gaps.push(daysBetween(g.deposits[i - 1].date, g.deposits[i].date));
    const medGap = median(gaps);
    const band = CADENCE_BANDS.find(b => medGap >= b.lo && medGap <= b.hi);
    if (!band) continue;
    const steadyGaps = gaps.filter(x => Math.abs(x - medGap) <= band.tol).length / gaps.length;
    const amounts = g.deposits.map(d => d.amount);
    const medAmt = median(amounts);
    const steadyAmts = amounts.filter(a => Math.abs(a - medAmt) <= AMOUNT_TOLERANCE * medAmt).length / amounts.length;
    if (steadyGaps >= GAP_STEADY_SHARE && steadyAmts >= AMOUNT_STEADY_SHARE) {
      return { ...base, type: "regular", cadence: band.cadence, source: g.key, reason: `${g.deposits.length} deposits from one source about every ${Math.round(medGap)} days, similar amounts` };
    }
  }
  return { ...base, type: "irregular", reason: `${rows.length} deposits over ${spanDays} days with no steady cadence or amounts` };
}

/** Manual override wins; detection decides otherwise; "unknown" keeps the regular view. */
export function resolveIncomeType(detectedType, override) {
  if (override === "irregular" || override === "regular") return { type: override, source: "override" };
  if (detectedType === "irregular") return { type: "irregular", source: "detected" };
  return { type: "regular", source: detectedType === "regular" ? "detected" : "default" };
}

/** Complete months of income history: [{ month:'YYYY-MM', total }], oldest first.
 *  The current month is excluded (partial), and so is any month that started
 *  before the data does (sinceDate = earliest transaction in the cache). */
export function monthlyIncomeHistory(deposits = [], { today, sinceDate = null, months = 6 } = {}) {
  const curMonth = String(today || "").slice(0, 7);
  if (!curMonth) return [];
  const firstFull = sinceDate ? (String(sinceDate).slice(8, 10) <= "03" ? String(sinceDate).slice(0, 7) : nextMonth(String(sinceDate).slice(0, 7))) : null;
  const totals = new Map();
  for (const d of deposits) {
    const m = d.date.slice(0, 7);
    if (m >= curMonth) continue;
    if (firstFull && m < firstFull) continue;
    totals.set(m, (totals.get(m) || 0) + d.amount);
  }
  // Months inside the window with no deposits are real $0 months for an
  // irregular earner; fill them so the range is honest.
  const out = [];
  let m = firstFull || (totals.size ? [...totals.keys()].sort()[0] : null);
  if (!m) return [];
  let guard = 0;
  while (m < curMonth && guard++ < 36) { out.push({ month: m, total: Math.round(totals.get(m) || 0) }); m = nextMonth(m); }
  return out.slice(-months);
}
function nextMonth(ym) { const [y, mo] = ym.split("-").map(Number); const d = new Date(Date.UTC(y, mo, 1)); return d.toISOString().slice(0, 7); }

/** Typical monthly range from complete months (p25..p75 + median), or null under 3 months. */
export function typicalMonthlyRange(history = []) {
  const totals = history.map(h => h.total);
  if (totals.length < 3) return null;
  return { low: Math.round(percentile(totals, 0.25)), high: Math.round(percentile(totals, 0.75)), median: Math.round(median(totals)), months: totals.length };
}

export function monthToDateIncome(deposits = [], today) {
  const m = String(today || "").slice(0, 7);
  return Math.round(deposits.filter(d => d.date.slice(0, 7) === m).reduce((s, d) => s + d.amount, 0));
}

/** 'above' | 'below' | 'on_pace' | null: month-to-date vs the typical median, prorated by day. */
export function monthTracking({ mtd, median: med, today }) {
  if (!med || !today) return null;
  const d = new Date(`${today}T00:00:00Z`);
  const dayOfMonth = d.getUTCDate();
  const daysInMonth = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  const expected = med * (dayOfMonth / daysInMonth);
  if (mtd >= expected * 1.15) return "above";
  if (mtd <= expected * 0.85) return "below";
  return "on_pace";
}

/** Everything the irregular view needs, in one call. */
export function irregularIncomeSummary(deposits = [], { today, sinceDate = null } = {}) {
  const history = monthlyIncomeHistory(deposits, { today, sinceDate });
  const typical = typicalMonthlyRange(history);
  const mtd = monthToDateIncome(deposits, today);
  return { mtd, typical, tracking: typical ? monthTracking({ mtd, median: typical.median, today }) : null, history };
}

/** The calendar's PAY days for a month. Empty unless the user actually set a
 *  schedule up, and never for an irregular earner. (The old inline version ran on
 *  the biweekly default and painted invented paydays for everyone.) */
export function scheduledPaydays(paySchedule, year, month, { setUp = false, incomeType = "regular" } = {}) {
  if (!setUp || incomeType === "irregular" || !paySchedule) return [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days = [];
  if (paySchedule.freq === "semimonthly") { days.push(Math.min(paySchedule.day1 || 1, daysInMonth)); days.push(Math.min(paySchedule.day2 || 15, daysInMonth)); }
  else if (paySchedule.freq === "monthly") { days.push(Math.min(paySchedule.day1 || 1, daysInMonth)); }
  else if (paySchedule.freq === "biweekly" || paySchedule.freq === "weekly") {
    const interval = paySchedule.freq === "weekly" ? 7 : 14;
    // The anchor is a local calendar date: new Date('YYYY-MM-DD') would parse it
    // as UTC midnight, which is the previous day west of UTC and put every
    // biweekly payday a day early (the old inline calendar had this bug).
    const [ay, am, ad] = String(paySchedule.startDate || "").split("-").map(Number);
    const anchor = (ay && am && ad) ? new Date(ay, am - 1, ad) : new Date(year, month, 1);
    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month, daysInMonth);
    let d = new Date(anchor);
    while (d > monthStart) d = new Date(d.getTime() - interval * DAY);
    while (d < monthStart) d = new Date(d.getTime() + interval * DAY);
    while (d <= monthEnd) { if (d.getMonth() === month) days.push(d.getDate()); d = new Date(d.getTime() + interval * DAY); }
  }
  return days;
}

// The line Kai sees for an irregular earner (chat + check-in), and the rule
// that travels with it. Numbers are allowed (behind the app lock); the
// check-in headline still goes through the lock-screen guard.
export function irregularIncomeLine(summary) {
  if (!summary) return "";
  const t = summary.typical;
  const typ = t ? `a typical month for them is $${t.low.toLocaleString()} to $${t.high.toLocaleString()} (median $${t.median.toLocaleString()}, from ${t.months} months)` : "not enough complete months yet to say what a typical month looks like";
  const track = summary.tracking === "above" ? "tracking above a typical month so far" : summary.tracking === "below" ? "tracking below a typical month so far" : summary.tracking === "on_pace" ? "about on pace for a typical month" : null;
  return `INCOME TYPE: IRREGULAR (no fixed paydays; paid per client/job). Received $${summary.mtd.toLocaleString()} so far this month; ${typ}${track ? `; ${track}` : ""}. RULE: never mention a payday, a paycheck date, or a "next" paycheck. Talk about this month's income against their typical range, and about cushion or runway when it is relevant.`;
}

// ── THE "HOW YOU'RE PAID" TOGGLE (2026-10-14 bug) ────────────────────────────
// A tester's screen showed "I'm paid irregularly · Set by you" while the row
// held 'regular'. The API logs showed six upserts in three minutes: each tap
// fired its own write, the UI was set by whichever RESPONSE came back, and the
// database kept whichever WRITE landed last. Two rapid taps could therefore
// disagree with the row forever, and nothing ever read the stored value back.
//
// createIncomeTypeSaver serializes taps (each write waits for the previous
// one), shows the tap optimistically, confirms from the row the server
// RETURNS, and reverts to the last confirmed value on failure.
//   write(value) -> Promise<{ ok: boolean, stored?: 'regular'|'irregular'|null }>
//   onState({ value, saving, confirmed, error }) is called on every change.
export const readIncomeTypeOverride = (row) =>
  (row?.income_type_override === "regular" || row?.income_type_override === "irregular") ? row.income_type_override : null;

export function createIncomeTypeSaver({ write, onState, initial = null }) {
  let confirmed = readIncomeTypeOverride({ income_type_override: initial });
  let shown = confirmed;
  let queue = Promise.resolve();
  let pending = 0;
  const emit = (extra = {}) => onState && onState({ value: shown, confirmed, saving: pending > 0, ...extra });
  return {
    get value() { return shown; },
    get confirmed() { return confirmed; },
    get saving() { return pending > 0; },
    choose(choice) {
      const next = choice === "regular" || choice === "irregular" ? choice : null;
      shown = next; pending++;
      emit();
      const run = queue.then(async () => {
        let res;
        try { res = await write(next); } catch (e) { res = { ok: false, error: e }; }
        pending--;
        if (res && res.ok) {
          // Trust the ROW, never the tap: the stored value is what the app shows.
          confirmed = readIncomeTypeOverride({ income_type_override: res.stored === undefined ? next : res.stored });
          if (pending === 0) shown = confirmed;
          emit();
          return true;
        }
        if (pending === 0) shown = confirmed; // revert to the last value the server confirmed
        emit({ error: res?.error || new Error("save failed") });
        return false;
      });
      queue = run.catch(() => {});
      return run;
    },
  };
}

// ── PAYDAY SET ONCE (2026-10-20) ─────────────────────────────────────────────
// Once the user has answered (irregular; or regular with the paycheck/schedule
// confirmed), the Finance tab shows one quiet line instead of the prompt and
// settings, and asks again only when the deposits stop matching.

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const ordinalDay = (n) => { const v = Number(n) || 1, t = v % 100; return `${v}${t >= 11 && t <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[v % 10] || 'th')}`; };
export const PAY_FREQ_DAYS = { weekly: 7, biweekly: 14, semimonthly: 15, monthly: 30 };

/** Is payday setup answered? Irregular: the user said so. Regular: a schedule is saved. */
export function paySetupDone({ incomeType, payScheduleSaved } = {}) {
  if (!incomeType) return false;
  if (incomeType.type === 'irregular') return incomeType.source === 'override';
  return incomeType.type === 'regular' && !!payScheduleSaved;
}

/** The quiet line: "Paid every other Friday", "Paid on the 1st and 15th", "Irregular income". */
export function payLine({ incomeType, paySchedule } = {}) {
  if (incomeType?.type === 'irregular') return 'Irregular income';
  const s = paySchedule || {};
  const wd = WEEKDAY_NAMES[Number(s.weekday ?? 5)] || 'Friday';
  switch (s.freq || 'biweekly') {
    case 'weekly': return `Paid every ${wd}`;
    case 'semimonthly': return `Paid on the ${ordinalDay(s.day1 || 1)} and ${ordinalDay(s.day2 || 15)}`;
    case 'monthly': return `Paid on the ${ordinalDay(s.day1 || 1)}`;
    default: return `Paid every other ${wd}`;
  }
}

/**
 * Has the pay pattern changed materially? True when the last paycheck is more
 * than 2x the usual gap ago. Paychecks = deposits of at least half the
 * confirmed paycheck (else half the largest deposit in the last 90 days). The
 * usual gap is the saved frequency's (regular) or the median gap between
 * paychecks (irregular, 3+ needed). No deposits = no evidence = no prompt.
 * @returns {{ changed: boolean, lastDate: string|null, gapDays: number|null }}
 */
export function payScheduleChanged({ deposits = [], today, type = 'regular', freq = 'biweekly', paycheckAmount = 0 } = {}) {
  const none = { changed: false, lastDate: null, gapDays: null };
  if (!today || !Array.isArray(deposits) || !deposits.length) return none;
  const recent = deposits.filter(d => d && daysBetween(d.date, today) <= 90);
  const floor = paycheckAmount > 0 ? paycheckAmount / 2 : Math.max(0, ...recent.map(d => d.amount)) / 2;
  const pays = deposits.filter(d => d && d.amount >= floor && d.date <= today).sort((a, b) => a.date.localeCompare(b.date));
  if (!pays.length) return none;
  const lastDate = pays[pays.length - 1].date;
  let gapDays = null;
  if (type === 'irregular') {
    const gaps = []; for (let i = 1; i < pays.length; i++) gaps.push(daysBetween(pays[i - 1].date, pays[i].date));
    if (pays.length < 3) return { changed: false, lastDate, gapDays: null };
    gapDays = median(gaps);
  } else gapDays = PAY_FREQ_DAYS[freq] || 14;
  return { changed: daysBetween(lastDate, today) > 2 * gapDays, lastDate, gapDays };
}
